/**
 * AR Aging Cards strip (Plan 14).
 *
 * 4 cards across the top of /billing/school — Current / 0–30 / 31–60 /
 * 60+. Clicking a card filters the invoice table to that bucket;
 * clicking the active card again clears the filter.
 */

import { Card } from '@ordo/ui/card'

import type { ArAgingBucket, SchoolARArAging } from '@/api/billing-school'
import { cn } from '@/lib/utils'

import { bucketLabel, formatCents } from './school-ar-helpers'

export interface ArAgingCardsProps {
  aging: SchoolARArAging
  activeBucket: ArAgingBucket | null
  onBucketSelect: (bucket: ArAgingBucket | null) => void
}

interface BucketDescriptor {
  key: ArAgingBucket
  amount: number
  count: number
  tone: 'neutral' | 'warning' | 'destructive'
}

export function ArAgingCards({ aging, activeBucket, onBucketSelect }: ArAgingCardsProps) {
  const buckets: BucketDescriptor[] = [
    { key: 'current', amount: aging.current, count: aging.counts.current, tone: 'neutral' },
    { key: 'days0to30', amount: aging.days0to30, count: aging.counts.days0to30, tone: 'warning' },
    { key: 'days31to60', amount: aging.days31to60, count: aging.counts.days31to60, tone: 'destructive' },
    { key: 'days60Plus', amount: aging.days60Plus, count: aging.counts.days60Plus, tone: 'destructive' },
  ]

  const handleSelect = (key: ArAgingBucket) => {
    if (activeBucket === key) {
      onBucketSelect(null)
    } else {
      onBucketSelect(key)
    }
  }

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {buckets.map((b) => {
        const isActive = activeBucket === b.key
        return (
          <Card
            key={b.key}
            role="button"
            tabIndex={0}
            aria-pressed={isActive}
            onClick={() => handleSelect(b.key)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                handleSelect(b.key)
              }
            }}
            className={cn(
              'flex cursor-pointer flex-col gap-1.5 p-4 transition-colors',
              'hover:border-foreground/30 hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              isActive && 'border-primary bg-primary/5 ring-1 ring-primary',
              b.tone === 'destructive' && !isActive && b.amount > 0 && 'border-destructive/60',
            )}>
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{bucketLabel(b.key)}</p>
            <p className="text-2xl font-semibold tabular-nums">{formatCents(b.amount, aging.currency)}</p>
            <p className="text-xs text-muted-foreground tabular-nums">
              {b.count} {b.count === 1 ? 'invoice' : 'invoices'}
            </p>
          </Card>
        )
      })}
    </div>
  )
}
